import DateFnsUtils from '@date-io/date-fns'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import {
  TimePicker as MuiTimePicker,
  TimePickerProps as MuiTimePickerProps,
  KeyboardDatePicker as MuiKeyboardDatePicker,
  KeyboardDatePickerProps as MuiKeyboardDatePickerProps,
  KeyboardTimePicker as MuiKeyboardTimePicker,
  KeyboardTimePickerProps as MuiKeyboardTimePickerProps,
  MuiPickersUtilsProvider as PickersUtilsProvider
} from '@material-ui/pickers'
import { MaterialUiPickersDate as MaterialUiPickersDateProps } from '@material-ui/pickers/typings/date'
import * as React from 'react'

export type TimePickerProps = MuiTimePickerProps
export type KeyboardDatePickerProps = MuiKeyboardDatePickerProps
export type KeyboardTimePickerProps = MuiKeyboardTimePickerProps
export type MaterialUiPickersDate = MaterialUiPickersDateProps

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      borderRadius: 3,
      padding: 0,
      '& > .MuiInputAdornment-positionEnd': {
        padding: theme.spacing(2.5),
        margin: 0
      },
      '& button': {
        padding: theme.spacing(1),
        position: 'absolute',
        right: theme.spacing(1)
      },
      '& svg': {
        width: theme.spacing(2.5),
        height: theme.spacing(2.5)
      }
    },
    input: {
      padding: theme.spacing(1, 1.5),
      ...theme.typography.body2
    },
    muiInputInput: {
      padding: 0
    },
    muiInputFocus: {
      border: 'inherits'
    },
    muiInputLabelRoot: {
      ...theme.typography.overline,
      marginBottom: theme.spacing(0.5),
      '&.shrink': {
        transform: 'inherit'
      }
    },
    MuiFormHelperTextRoot: {
      ...theme.typography.caption,
      marginTop: theme.spacing(0.5)
    },
    muiInputLabelFormControl: {
      position: 'static'
    },
    iconButton: {
      '& > div > div > button': {
        padding: theme.spacing(0.5),
        marginRight: theme.spacing(-0.5)
      }
    }
  })
)

const inputProps = (classes: any): object => ({
  classes: {
    root: classes.root,
    input: classes.input
  },
  notched: false
})

const inputLabelProps = (classes: any): object => ({
  classes: {
    root: classes.muiInputLabelRoot,
    formControl: classes.muiInputLabelFormControl,
    shrink: 'shrink'
  },
  shrink: true
})

const formHelperTextProps = (
  classes: any,
  FormHelperTextProps: any
): object => {
  const formHelperTextPropsClasses = FormHelperTextProps.classes
  return {
    ...FormHelperTextProps,
    classes: {
      root: classes.MuiFormHelperTextRoot,
      ...formHelperTextPropsClasses
    }
  }
}

const TimePicker: React.FC<TimePickerProps> = (props) => {
  const classes = useStyles()
  const { FormHelperTextProps = {}, children, ...rest } = props
  return (
    <MuiTimePicker
      InputProps={inputProps(classes)}
      InputLabelProps={inputLabelProps(classes)}
      FormHelperTextProps={formHelperTextProps(classes, FormHelperTextProps)}
      inputVariant="outlined"
      {...rest}
    >
      {children}
    </MuiTimePicker>
  )
}

const KeyboardDatePicker: React.FC<KeyboardDatePickerProps> = (props) => {
  const { FormHelperTextProps = {}, children, ...rest } = props
  const classes = useStyles()
  return (
    <MuiKeyboardDatePicker
      InputProps={inputProps(classes)}
      InputLabelProps={inputLabelProps(classes)}
      FormHelperTextProps={formHelperTextProps(classes, FormHelperTextProps)}
      className={classes.iconButton}
      inputVariant="outlined"
      {...rest}
    >
      {children}
    </MuiKeyboardDatePicker>
  )
}

const KeyboardTimePicker: React.FC<KeyboardTimePickerProps> = (props) => {
  const { FormHelperTextProps = {}, children, ...rest } = props
  const classes = useStyles()
  return (
    <MuiKeyboardTimePicker
      InputProps={inputProps(classes)}
      InputLabelProps={inputLabelProps(classes)}
      FormHelperTextProps={formHelperTextProps(classes, FormHelperTextProps)}
      className={classes.iconButton}
      inputVariant="outlined"
      {...rest}
    >
      {children}
    </MuiKeyboardTimePicker>
  )
}

export {
  TimePicker,
  KeyboardDatePicker,
  KeyboardTimePicker,
  PickersUtilsProvider,
  DateFnsUtils
}